// vue
import store from '@/store';
import { ref } from '@vue/composition-api';

// config
import airtableLayers from '@/vue3/configs/airtableLayers';

// airtable
const Airtable = require('airtable');

// mapbox
import mapboxgl from 'mapbox-gl';

export const useAirtable = () => {
    // ref
    const baseValue = ref();
    const airtableRecords = ref();

    const isAirtableAvailable = ref(false);

    // methods
    const getAirtableData = async (): Promise<void> => {
        baseValue.value = new Airtable({
            apiKey: `${store.state.airtableApiKey}`,
        }).base(`${store.state.airtableBaseKey}`);

        try {
            airtableRecords.value = await baseValue
                .value(`${store.state.airtableTableName}`)
                .select()
                .all();
        } catch (error) {
            isAirtableAvailable.value = false;
            console.log(error);
            return;
        }

        isAirtableAvailable.value = true;
        airtableRecords.value = airtableRecords.value.filter((record) => {
            return Object.keys(record.fields).length !== 0;
        });
    };

    const generateMapboxAirtableFeature = (airtableRecord: any) => {
        return {
            id: airtableRecord.fields.Tag,
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [
                    airtableRecord.fields.Longitude,
                    airtableRecord.fields.Latitude,
                ],
            },
            properties: {
                title: airtableRecord.Tag,
                color: airtableRecord.fields.Color,
                label: airtableRecord.fields.Label,
                isVisible: true,
                id: airtableRecord.Tag,
                description:
                    '<div style="padding:1rem;"><b>' +
                    '<b>Tag:</b> ' +
                    airtableRecord.fields.Tag +
                    '<br/>' +
                    '<b>Model:</b> ' +
                    airtableRecord.fields.Model +
                    '<br/>' +
                    [
                        airtableRecord.fields.Type[0] ===
                            'Internal Access Point' ||
                        airtableRecord.fields.Type[0] ===
                            'External Access Point' ||
                        airtableRecord.fields.Type[0] === 'Switch'
                            ? '<b>MAC:</b> ' +
                              airtableRecord.fields.MAC[0] +
                              '<br/>' +
                              '<b>Serial No:</b> ' +
                              airtableRecord.fields['Serial Number'][0]
                            : '',
                    ] +
                    '<br/></b></div>',
            },
        };
    };

    const generateAirtableBufferFeature = (airtableRecord: any) => {
        let ret = <any>[];
        let theta: any, x: any, y: any;
        let points = 64;
        let airtableBufferValue: any;

        if (
            airtableRecord.fields.Type === 'Internal Access Point' ||
            airtableRecord.fields.Type === 'External Access Point'
        ) {
            airtableBufferValue = parseInt(airtableRecord.fields.Buffer);
        } else {
            airtableBufferValue = 0;
        }

        if (!points) points = 64;

        let coords = {
            latitude: parseFloat(airtableRecord.fields.Latitude),
            longitude: parseFloat(airtableRecord.fields.Longitude),
        };

        let km = airtableBufferValue / 1000;

        let distanceX =
            km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
        let distanceY = km / 110.574;

        for (let i = 0; i < points; i++) {
            theta = (i / points) * (2 * Math.PI);
            x = distanceX * Math.cos(theta);
            y = distanceY * Math.sin(theta);

            ret.push([coords.longitude + x, coords.latitude + y]);
        }
        ret.push(ret[0]);

        return {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [ret],
            },
            properties: {
                isVisible: true,
            },
        };
    };

    const initAirtableBufferLayerSource = (
        map: mapboxgl.Map,
        layerTreeState: any,
    ): void => {
        const airtableBufferSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: airtableRecords.value.map((x) => {
                    const airtableRecord = generateAirtableBufferFeature(x);
                    airtableRecord.properties.isVisible =
                        layerTreeState['airtable-buffer-layer'].isVisible;
                    return airtableRecord;
                }),
            },
        };

        if (map.getSource('air-table-buffer-layer')) {
            map.removeSource('air-table-buffer-layer');
        }

        map.addSource('air-table-buffer-layer', airtableBufferSource);
    };

    const initAirtableRecordSource = (
        map: mapboxgl.Map,
        layerTreeState: any,
    ) => {
        const airtableSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: airtableRecords.value.map((x) => {
                    const airtableRecord = generateMapboxAirtableFeature(x);
                    airtableRecord.properties.isVisible =
                        layerTreeState['airtable-records'].isVisible;
                    return airtableRecord;
                }),
            },
        };

        if (map.getSource('air-table-records')) {
            map.removeSource('air-table-records');
        }

        map.addSource('air-table-records', airtableSource);

        initAirtableBufferLayerSource(map, layerTreeState);
    };

    const initAirtableLayers = (map) => {
        airtableLayers.forEach((airtableLayer) => {
            if (!map.getSource(airtableLayer.source)) return;
            map.addLayer(airtableLayer);
        });
    };

    const removeAirtableLayers = (map) => {
        airtableLayers.forEach((airtableLayer) => {
            map.removeLayer(airtableLayer.id);
        });
    };

    const setAirtableRecordVisibility = (
        map: mapboxgl.Map,
        isVisible: boolean,
    ) => {
        if (isVisible === undefined) {
            isVisible = true;
        }

        const airtableRecordSource = map.getSource('air-table-records');

        const features = airtableRecordSource._data.features;

        features.forEach((feature) => {
            feature.properties.isVisible = isVisible;
        });

        airtableRecordSource.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const setAirtableRecordBufferVisibility = (
        map: any,
        isVisible: boolean,
    ) => {
        if (isVisible === undefined) {
            isVisible = true;
        }

        const airtableBufferRecordSource = map.getSource(
            'air-table-buffer-layer',
        );

        const airtableFeatures = airtableBufferRecordSource._data.features;

        airtableFeatures.forEach((airtableFeature) => {
            airtableFeature.properties.isVisible = isVisible;
        });

        airtableBufferRecordSource.setData({
            type: 'FeatureCollection',
            features: airtableFeatures,
        });
    };

    const airtableNodeToggle = (
        x: any,
        mapboxLayerState: any,
        map: any,
        isVisible: boolean,
    ) => {
        if (x.children && x.children.length > 0) {
            x.children.forEach((x: any) => {
                airtableNodeToggle(x, mapboxLayerState, map, isVisible);
            });
        } else {
            if (x.layer === 'airtable-records') {
                mapboxLayerState[x.layer].isVisible = isVisible;
                setAirtableRecordVisibility(map, isVisible);
            } else if (x.layer === 'airtable-buffer-layer') {
                mapboxLayerState[x.layer].isVisible = isVisible;
                setAirtableRecordBufferVisibility(map, isVisible);
            }
        }
    };

    const initAirtableSourceAndLayer = (
        map: mapboxgl.Map,
        mapboxLayerState: any,
    ): void => {
        if (!isAirtableAvailable.value) return;

        initAirtableRecordSource(map, mapboxLayerState);
        initAirtableLayers(map);
    };

    return {
        getAirtableData,
        airtableNodeToggle,
        initAirtableLayers,
        isAirtableAvailable,
        removeAirtableLayers,
        initAirtableRecordSource,
        initAirtableSourceAndLayer,
        setAirtableRecordVisibility,
        setAirtableRecordBufferVisibility,
    };
};
